'use client';

import type { ILocationResult } from '@components/searchCity';
import { FLIGHT_SEARCH_STORAGE } from '@config/storageKeys';
import type { LeagueEvent } from '@interfaces/APITravel';
import useSWR, { preload } from 'swr';
import {
  GEOCODE_RETRY_DELAY_MS,
  resolveAirportPin,
  resolveDowntownLocation,
  venueCityName,
  type HotelMapPin,
} from '../hotel-search-area';

type CachedAirport = {
  AirportCode: string;
  AirportName?: string;
  City?: string;
};

const SWR_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: true,
  errorRetryCount: 3,
  errorRetryInterval: GEOCODE_RETRY_DELAY_MS,
  dedupingInterval: 60_000,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function asAirport(value: unknown): CachedAirport | null {
  if (!isRecord(value) || typeof value.AirportCode !== 'string') return null;
  if (!value.AirportCode) return null;
  return {
    AirportCode: value.AirportCode,
    AirportName:
      typeof value.AirportName === 'string' ? value.AirportName : undefined,
    City: typeof value.City === 'string' ? value.City : undefined,
  };
}

function readJson(key: string): unknown {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readCachedDestinationAirport(eventId?: number): CachedAirport | null {
  if (eventId) {
    for (const kind of ['sport', 'concert']) {
      const parsed = readJson(`airport_cache_${eventId}_${kind}`);
      if (!isRecord(parsed)) continue;
      const airport = asAirport(parsed.data);
      if (airport) return airport;
    }
  }
  const flightSearch = readJson(FLIGHT_SEARCH_STORAGE);
  if (!isRecord(flightSearch)) return null;
  const state = isRecord(flightSearch.state)
    ? flightSearch.state
    : flightSearch;
  return asAirport(state.destinationAirport) ?? asAirport(state);
}

export function hotelDowntownSearchKey(
  city: string,
  region: string,
  country: string
) {
  const normalizedCity = city.trim().toLowerCase();
  if (!normalizedCity) return null;
  return [
    'hotel-search-downtown',
    normalizedCity,
    region.trim().toLowerCase(),
    country.trim().toLowerCase(),
  ] as const;
}

export function prefetchHotelSearchArea(
  event?: LeagueEvent | null
): Promise<ILocationResult | null> {
  const city = venueCityName(event);
  const region = event?.venue?.address?.region?.trim() ?? '';
  const country = event?.venue?.address?.country_code?.trim() ?? '';
  if (!city) return Promise.resolve(null);
  const key = hotelDowntownSearchKey(city, region, country);
  if (!key) return Promise.resolve(null);
  try {
    // Warms both the SWR cache (instant useSWR on /search-hotel) and the
    // sessionStorage downtown cache inside resolveDowntownLocation.
    return preload(key, () =>
      resolveDowntownLocation({ city, region, country })
    ).catch(() =>
      resolveDowntownLocation({ city, region, country }).catch(() => null)
    );
  } catch {
    return resolveDowntownLocation({ city, region, country }).catch(() => null);
  }
}

export function useHotelSearchArea(event?: LeagueEvent | null) {
  const city = venueCityName(event);
  const region = event?.venue?.address?.region?.trim() ?? '';
  const country = event?.venue?.address?.country_code?.trim() ?? '';
  const airport = readCachedDestinationAirport(event?.id);

  const downtown = useSWR<ILocationResult | null>(
    hotelDowntownSearchKey(city, region, country),
    () => resolveDowntownLocation({ city, region, country }),
    SWR_OPTIONS
  );

  const airportPin = useSWR<HotelMapPin | null>(
    airport?.AirportCode
      ? [
          'hotel-search-airport-pin',
          airport.AirportCode,
          airport.AirportName ?? '',
          airport.City ?? '',
        ]
      : null,
    ([, airportCode, airportName, cityName]: [
      string,
      string,
      string,
      string,
    ]) =>
      resolveAirportPin({
        airportCode,
        airportName,
        city: cityName,
      }),
    SWR_OPTIONS
  );

  return {
    city,
    downtown: downtown.data ?? null,
    airport: airportPin.data ?? null,
    isDowntownLoading: Boolean(city) && downtown.isLoading,
    isAirportLoading: Boolean(airport?.AirportCode) && airportPin.isLoading,
  };
}
